import { For, Match, ParentProps, Setter, Show, Switch, createContext, createEffect, createMemo, createSignal, onMount } from 'solid-js'
import { requireContext, toast } from 'common'
import { DocumentReaderContext } from './DocumentReaderContext'
import styles from './st-document-reader.module.css'
import Button from '../st-button/st-button';
import { STComponentWrapperProps } from '../st-component-wrapper/st-component-wrapper';
import { formatDob, formatHeight } from '../st-visitor-checkin/fragments/util';
import { DocumentScanResult } from 'st-app/api';
import Loader from '../st-loader/st-loader';
import Wizard from '../st-wizard/st-wizard';
import { useWizardContext } from '../st-wizard/WizardContext';
import Select from '../st-select/st-select';

const DocumentReaderContextJsx = createContext<DocumentReaderContext>();
export const requireDocumentReaderContext = () => {
    return requireContext(DocumentReaderContextJsx);
}

export class DocumentReaderProps extends STComponentWrapperProps {
    setValue?: (value: any) => Setter<string>
    scan?: DocumentScanResult
    onScan?: (value: DocumentScanResult) => void
    ref?: (handle: DocumentReaderContext) => void
}

export default function DocumentReader(props: ParentProps<Omit<DocumentReaderProps, 'componentName'>>) {
    const context = new DocumentReaderContext(props.scan, props.onScan);

    props?.ref?.(context);

    return (
        <DocumentReaderContextJsx.Provider value={context}>
            <DocumentReaderInternal {...props} />
        </DocumentReaderContextJsx.Provider>
    )
}

function DocumentReaderInternal(props: ParentProps<Omit<DocumentReaderProps, 'componentName'>>) {
    const context = requireDocumentReaderContext()
    const wizardContext = useWizardContext();

    onMount(async () => {
        await context.init();
    });

    const statusMessage = createMemo(() => {
        switch (context.status()) {
            case 'pending': return 'Waiting for user to initiate scan...';
            case 'reading': return 'Reading document...';
            case 'success': return 'Document read successfully.';
            case 'error': return 'An error occurred while reading the document. Please try again.';
        }
    });

    const bridgeStatusMessage = createMemo(() => {
        const vmStatus = context.vmBridgeStatus();
        if (!vmStatus) return null;
        
        if (vmStatus.status === 'not_running') {
            return 'Visitor management bridge is not running. Please ensure the service is started and try again.';
        }
        if (vmStatus.status === 'outdated') {
            return `Visitor management bridge is outdated (${vmStatus.currentVersion} → ${vmStatus.latestVersion}). Please update the bridge.`;
        }
        if (vmStatus.health === 'unhealthy') {
            return 'Visitor management bridge is experiencing issues. Please check the service status and try again.';
        }
        return null;
    });

    const tryScan = async () => {
        try {
            await context.read();

            if (context.photoBase64() || context.aamva()) {
                props.setValue?.(JSON.stringify(context.scan()));
            }
        } catch (error) {
            toast.notify('error', 'Failed to connect to document reader. Please ensure the device is connected and try again.');
            console.error('Failed to connect to document reader:', error);
        }
    }

    return (
        <div class={styles['st-document-reader']}>
            <div style='display: flex; flex-direction: column;'>
                <Switch>
                    <Match when={context.status() === 'pending'}>
                        <div style='min-height: 350px; flex-direction: column; display: flex; justify-content: center; align-items: center; gap: 8px;'>
                            <Show when={context.scannersLoading()}>
                                <Loader />
                            </Show>

                            <Show when={!context.scannersLoading()}>
                                <Show when={bridgeStatusMessage()}>
                                    <span class={styles.error}>{bridgeStatusMessage()}</span>
                                    <Button icon='fas fa-download'>
                                        <a download="" href={'dist/SynergyVisitorManagementBridge-Setup.exe'}>Download Latest Installer</a>
                                    </Button>
                                </Show>

                                <Show when={context.scanners().length === 0 && !bridgeStatusMessage()}>
                                    <span>No document scanners detected. Ensure that the visitor management bridge is installed and try again.</span>
                                    <a download="" href={'dist/SynergyVisitorManagementBridge-Setup.exe'}>Download Installer</a>
                                </Show>

                                <Show when={context.scanners().length > 1 && !bridgeStatusMessage()}>
                                    <Select value={[context.selectedScannerId, context.setSelectedScannerId]}>
                                        <For each={context.scanners()}>{(scanner) => (
                                            <Select.Option value={scanner.id}>
                                                <span>{scanner.displayName}</span>
                                            </Select.Option>
                                        )}</For>
                                    </Select>
                                </Show>

                                <Show when={!bridgeStatusMessage() && context.scanners().length > 0}>
                                    <Show when={!wizardContext} fallback={<Wizard.Button icon='far fa-barcode-scan' label='Scan' size='x-large' onClick={tryScan} />}>
                                        <Button onClick={tryScan}>Scan</Button>
                                    </Show>
                                </Show>
                            </Show>

                            
                        </div>
                    </Match>
                    <Match when={context.status() !== 'success' && context.status() !== 'error'}>
                        <div style='min-height: 350px; flex-direction: column; display: flex; justify-content: center; align-items: center; gap: 8px;'>
                            <Show when={context.status() !== 'pending'}>
                                <Loader />
                            </Show>

                            <span>{statusMessage()}</span>
                        </div>
                    </Match>
                    <Match when={context.status() === 'error'}>
                        <div style='flex-direction: column; display: flex; justify-content: center; align-items: center; gap: 8px;'>
                            <span class={styles.error}>{statusMessage()}</span>
                            <Button onClick={() => tryScan()}>Retry</Button>
                        </div>
                    </Match>
                    <Match when={context.status() === 'success'}>
                        <div style='display: grid; grid-template-columns: 1fr 1fr; gap: 32px;'>
                            <div style='height: 350px;'>
                                <Show when={context.photoBase64()} fallback={<span>Waiting for scan</span>}>
                                    <img style='height: 350px; border-radius: 8px;' src={`data:image/bmp;base64,${context.photoBase64()}`}></img>
                                </Show>
                            </div>

                            <div style='display: flex; flex-direction: column; gap: 8px;'>
                                <h1>Scanned Data</h1>

                                <Field label='First Name' value={context.aamva()?.firstName || 'N/A'} />
                                <Field label='Last Name' value={context.aamva()?.lastName || 'N/A'} />
                                <Field label='Date of Birth' value={formatDob(context.aamva()?.dateOfBirth) || 'N/A'} />
                                <Field label='Document Number' value={context.aamva()?.idNumber || 'N/A'} />
                                <Field label='Issuing State' value={context.aamva()?.state || 'N/A'} />
                                <Field label='Issue Date' value={formatDob(context.aamva()?.issueDate) || 'N/A'} />
                                <Field label='Expiration Date' value={formatDob(context.aamva()?.expirationDate) || 'N/A'} />
                                <Field label='Height' value={formatHeight(context.aamva()?.heightInches) || 'N/A'} />
                                <div style='flex-grow: 1'></div>
                                <Button style='align-self: center' icon='fas fa-refresh' onClick={() => tryScan()}>Scan Again</Button>
                            </div>

                            <div style='grid-column: 1/3; flex-direction: column; display: flex; justify-content: center; align-items: center; gap: 8px;'>
                            </div>
                        </div>
                    </Match>
                </Switch>
            </div>
        </div>
    );
}

const Field = (props: { label: string, value: string }) => {
    return (
        <div class={styles.field}>
            <span class={styles.label}>{props.label}:</span>
            <span class={styles.value}>{props.value}</span>
        </div>
    );
}

DocumentReader.defaultProps = new DocumentReaderProps();